import type { DeepString } from "@/lib/i18n";

/**
 * CContactForm — polskie tłumaczenia.
 *
 * Wartości używane jako fallback gdy backend `content.lang.*` nie dostarczy
 * stringa (backend ZAWSZE powinien dostarczyć — to są safety nets) + jako
 * komunikaty walidacyjne (zod messages) + statusy systemowe (rate limit,
 * timeout, anti-spam delay).
 */
export const pl = {
  /* ─── Konfiguracja formularza (system errors) ─── */
  noConfig: "Brak danych konfiguracji formularza.",
  /** Tryb CRM bez aktywnego modułu. */
  crmModeWithoutModule: {
    body1: "Formularz jest skonfigurowany w trybie wysyłki do CRM, ale moduł",
    moduleName: "m_crm_sales_support",
    body2:
      'nie jest aktywny w tej domenie. Włącz moduł w panelu administracyjnym lub zmień tryb wysyłki na "email" w konfiguracji katalogu.',
  },
  /** Nieznany tryb wysyłki. */
  unknownSendMode: (mode: string) =>
    `Nieznany tryb wysyłki w konfiguracji formularza: ${mode}.`,

  /* ─── Backend fallback labels (preferuj backend `content.lang.*`) ─── */
  contactUsingForm: "Formularz kontaktowy",
  labels: {
    text: "Treść",
  },
  send: "Wyślij",
  sending: "Wysyłanie…",

  /* ─── Nagłówki sekcji pól (grupowanie personal/company) ─── */
  sectionHeaders: {
    personal: "Dane osobowe",
    company: "Dane firmowe",
  },

  /* ─── Walidacja (zod messages) ─── */
  validation: {
    /** `requiredField('Email')` -> `Pole "Email" jest wymagane.` */
    requiredField: (label: string) => `Pole "${label}" jest wymagane.`,
    invalidEmail: "Niepoprawny adres email.",
    invalidUrl: "Adres URL musi zaczynać się od http:// lub https://.",
    invalidPhone: "Niepoprawny numer telefonu.",
    invalidNip: "NIP musi mieć 10 cyfr.",
    requiredText: 'Pole "Treść" jest wymagane.',
    requiredPersonalData: "Wymagana zgoda na przetwarzanie danych osobowych.",
  },

  /* ─── Mapa (MapView) ─── */
  map: {
    /** CTA "Wskazówki dojazdu" w GoogleMapView (link do Google Maps directions). */
    directionsLink: "Wskazówki dojazdu ->",
    /** CTA "Otwórz w OpenStreetMap" w OpenStreetMapView (link do OSM full map). */
    openInOpenStreetMap: "Otwórz w OpenStreetMap ->",
  },

  /* ─── Statusy submitu / errory systemowe ─── */
  status: {
    /** `waitBeforeSubmit(seconds)` -> `Poczekaj jeszcze N sekund przed wysłaniem.` */
    waitBeforeSubmit: (seconds: number) =>
      `Poczekaj jeszcze ${seconds} sekund przed wysłaniem.`,
    rateLimit: "Zbyt wiele prób z tego adresu IP. Spróbuj za chwilę.",
    sendFailed: "Nie udało się wysłać wiadomości.",
    sendSuccess: "Wiadomość została wysłana, dziękujemy!",
    /** `networkError(message)` -> `Błąd sieci: ${message}` */
    networkError: (message: string) => `Błąd sieci: ${message}`,
    networkErrorGeneric: "Błąd sieci podczas wysyłania.",
  },
} as const;

export type CContactFormTranslations = DeepString<typeof pl>;
